// REPORTDASH1: the location console's dashboard source. One request, GET /tenants/:tenantId/reports/dashboard (src/domain/
// reportsDashboard.ts), whose parts arrive only for what this person's permissions allow. This file words them: the figure, its
// period and its confidence, the day-one lines, and the Needs attention rows with the one place that fixes each.
import { record } from "./cannedReportModel.ts";
import {
  CHIP,
  ATTENTION_LIMIT,
  count,
  dayRange,
  dollars,
  empty,
  emptyPreview,
  failed,
  failedPreview,
  int,
  isPart,
  plural,
  ready,
  shortDay,
  text,
  weekdayDay,
  wholeDollars,
  type AttentionRow,
  type DashLoad,
  type DashboardSource,
  type FigureDef,
  type FigureView,
  type PreviewDef,
  type PreviewView,
} from "./reportsDashboardModel.ts";

export const LOCATION_FIGURES: readonly FigureDef[] = [
  { id: "net-sales", title: "Net sales today", hint: "Sales summary, today", open: [{ kind: "canned", id: "sales-summary", preset: "today" }, { kind: "external", id: "sales-by-day" }] },
  { id: "tips", title: "Tips today", hint: "Sales summary, today", open: [{ kind: "canned", id: "sales-summary", preset: "today" }, { kind: "external", id: "sales-by-day" }] },
  { id: "sales-tax", title: "Sales tax this month", hint: "Sales tax worksheet, this month", open: [{ kind: "external", id: "sales-tax" }] },
  { id: "open-orders", title: "Open orders", hint: "Open purchase orders", open: [{ kind: "canned", id: "current-open-purchase-orders" }] },
  { id: "low-stock", title: "Low stock", hint: "Inventory value, items below par", open: [{ kind: "canned", id: "current-inventory-value" }] },
  { id: "inventory-value", title: "Inventory value", hint: "Inventory value, on hand now", open: [{ kind: "canned", id: "current-inventory-value" }] },
  { id: "no-cost", title: "Items with no cost", hint: "Inventory value, items left out", open: [{ kind: "canned", id: "current-inventory-value" }] },
];
export const LOCATION_PREVIEWS: readonly PreviewDef[] = [
  { id: "daily-sales", title: "Daily sales", hint: "Net sales for the last 14 days", open: [{ kind: "canned", id: "daily-sales", preset: "custom" }, { kind: "external", id: "sales-by-day" }] },
  { id: "product-mix", title: "Product mix", hint: "Top 5 drinks this week", open: [{ kind: "canned", id: "product-mix", preset: "last_7_days" }] },
  { id: "open-purchase-orders", title: "Open purchase orders", hint: "Placed and part received", open: [{ kind: "canned", id: "current-open-purchase-orders" }] },
];
export const LOCATION_DEFAULTS = {
  figures: ["net-sales", "tips", "sales-tax", "open-orders", "low-stock", "inventory-value"],
  previews: ["daily-sales", "product-mix"],
} as const;

const STOCK_ACCESS = ["inv.vendor_offer.manage", "admin.liquor_product.view", "admin.liquor_product.manage", "inv.count_section.manage", "admin.menu_drink.manage", "admin.location_settings.manage"] as const;
const ORDER_DAYS = 7;

function names(list: unknown): string[] {
  return Array.isArray(list) ? list.filter((entry): entry is string => typeof entry === "string") : [];
}
function nameList(listed: string[], total: number): string {
  if (listed.length === 0) return plural(total, "item");
  const rest = total - listed.length;
  const head = listed.length <= 1 ? listed.join("") : `${listed.slice(0, -1).join(", ")} and ${listed[listed.length - 1] as string}`;
  return rest > 0 ? `${listed.join(", ")} and ${count(rest)} more` : head;
}

function salesFigures(part: unknown, tax: unknown): Record<string, FigureView> {
  const out: Record<string, FigureView> = {};
  if (part !== undefined) {
    if (!isPart(part) || part.status !== "ok" || !record(part["today"])) {
      out["net-sales"] = failed();
      out["tips"] = failed();
    } else {
      const today = part["today"];
      const tickets = int(today["tickets"]);
      const net = int(today["netCents"]);
      const tips = int(today["tipsCents"]);
      const open = today["status"] === "open";
      const chip = open ? CHIP.open() : CHIP.exact();
      const when = open ? "Today so far" : "Today";
      if (tickets === undefined || net === undefined || tips === undefined) {
        out["net-sales"] = failed();
        out["tips"] = failed();
      } else if (tickets === 0) {
        out["net-sales"] = empty("No sales today yet", "Shows after the first paid ticket today.");
        out["tips"] = empty("No tips today yet", "Shows with the first paid ticket today.");
      } else {
        out["net-sales"] = ready(dollars(net), when, chip);
        out["tips"] = ready(dollars(tips), when, chip);
      }
    }
  }
  if (tax !== undefined) {
    if (!isPart(tax) || tax.status !== "ok") out["sales-tax"] = failed();
    else if (tax["month"] === null) out["sales-tax"] = empty("No closed day this month yet", "Shows after the first day is closed.");
    else if (record(tax["month"]) && int(tax["month"]["cents"]) !== undefined && typeof tax["month"]["from"] === "string" && typeof tax["month"]["to"] === "string")
      out["sales-tax"] = ready(dollars(tax["month"]["cents"] as number), dayRange(tax["month"]["from"], tax["month"]["to"]), CHIP.exact());
    else out["sales-tax"] = failed();
  }
  return out;
}
function orderFigure(orders: unknown): FigureView {
  if (!isPart(orders) || orders.status !== "ok") return failed();
  const open = int(orders["openCount"]);
  const total = int(orders["estimatedTotalCents"]);
  if (open === undefined || total === undefined) return failed();
  if (open === 0) return empty("No open orders", "Start one in Purchasing.");
  return ready(count(open), total > 0 ? `${dollars(total)} ordered, estimated` : "Placed or part received", CHIP.live());
}
function stockFigures(stock: unknown): Record<string, FigureView> {
  if (!isPart(stock) || stock.status !== "ok")
    return { "low-stock": failed(), "inventory-value": failed(), "no-cost": failed() };
  const items = int(stock["items"]);
  const withPar = int(stock["withPar"]);
  const counted = int(stock["countedItems"]);
  const value = int(stock["valueCents"]);
  const noCost = int(stock["noCost"]);
  const belowPar = int(stock["belowPar"]);
  if ([items, withPar, counted, value, noCost, belowPar].some((n) => n === undefined))
    return { "low-stock": failed(), "inventory-value": failed(), "no-cost": failed() };
  const lastCount = text(stock["lastCountDate"]);
  const basis = lastCount === undefined || lastCount === "" ? CHIP.live("On hand now") : CHIP.count(`From count of ${shortDay(lastCount)}`);
  return {
    "low-stock":
      (items as number) === 0
        ? empty("No items yet", "Add the items you stock.")
        : (withPar as number) === 0
          ? empty("Needs par levels", "Set par on an item first.")
          : ready(count(belowPar as number), "items below par", basis),
    "inventory-value":
      (counted as number) === 0
        ? empty("Not set up", "Enter opening stock.")
        : ready(dollars(value as number), "On hand now", (noCost as number) > 0 ? CHIP.partial(`${plural(noCost as number, "item")} no cost`) : basis),
    "no-cost":
      (items as number) === 0
        ? empty("No items yet", "Add the items you stock.")
        : ready(count(noCost as number), "items with no cost", basis),
  };
}

function previews(dto: Record<string, unknown>): Record<string, PreviewView> {
  const out: Record<string, PreviewView> = {};
  const sales = dto["sales"];
  if (sales !== undefined) {
    if (!isPart(sales) || sales.status !== "ok" || !Array.isArray(sales["days"])) out["daily-sales"] = failedPreview();
    else {
      const days = sales["days"].filter(record).filter((day) => typeof day["businessDate"] === "string" && int(day["netCents"]) !== undefined && int(day["tickets"]) !== undefined);
      const first = days[0];
      const last = days[days.length - 1];
      const filters = first === undefined || last === undefined ? [] : [dayRange(first["businessDate"] as string, last["businessDate"] as string), "Net sales"];
      if (first === undefined || last === undefined || days.every((day) => day["tickets"] === 0))
        out["daily-sales"] = emptyPreview(filters, "No sales to chart yet", "The daily chart appears after the first paid ticket.");
      else {
        const best = days.reduce((a, b) => ((b["netCents"] as number) > (a["netCents"] as number) ? b : a));
        const open = last["status"] === "open";
        out["daily-sales"] = {
          state: "ready",
          filters,
          body: {
            kind: "chart",
            points: days.map((day) => ({ label: `${shortDay(day["businessDate"] as string)}, ${dollars(day["netCents"] as number)}`, value: day["netCents"] as number, highlight: day === last })),
            first: shortDay(first["businessDate"] as string),
            last: shortDay(last["businessDate"] as string),
            summary: `Best day: ${weekdayDay(best["businessDate"] as string)}, ${wholeDollars(best["netCents"] as number)}`,
          },
          chip: open ? CHIP.open("Today so far, open day") : CHIP.exact(),
          range: { from: first["businessDate"] as string, to: last["businessDate"] as string },
          emptyTitle: "",
          emptyText: "",
        };
      }
    }
  }
  const mix = dto["productMix"];
  if (mix !== undefined) {
    const from = isPart(mix) ? text(mix["from"]) : undefined;
    const to = isPart(mix) ? text(mix["to"]) : undefined;
    if (!isPart(mix) || mix.status !== "ok" || !Array.isArray(mix["rows"]) || from === undefined || to === undefined) out["product-mix"] = failedPreview();
    else {
      const rows = mix["rows"].filter(record).filter((row) => typeof row["name"] === "string" && int(row["quantity"]) !== undefined && int(row["netCents"]) !== undefined);
      const filters = [dayRange(from, to), "Top 5 by quantity"];
      out["product-mix"] =
        rows.length === 0
          ? emptyPreview(filters, "No drinks sold this week", "The top five show after the first paid ticket.")
          : {
              state: "ready",
              filters,
              body: { kind: "table", columns: ["Item", "Sold", "Net sales"], rows: rows.map((row) => [row["name"] as string, count(row["quantity"] as number), dollars(row["netCents"] as number)]) },
              chip: CHIP.open("Includes today so far"),
              emptyTitle: "",
              emptyText: "",
            };
    }
  }
  const orders = dto["orders"];
  if (orders !== undefined) {
    const filters = ["Any date", "Placed, part received"];
    if (!isPart(orders) || orders.status !== "ok" || !Array.isArray(orders["rows"])) out["open-purchase-orders"] = failedPreview();
    else {
      const rows = orders["rows"].filter(record).filter((row) => typeof row["vendorName"] === "string" && int(row["estimatedTotalCents"]) !== undefined);
      out["open-purchase-orders"] =
        rows.length === 0
          ? emptyPreview(filters, "No open orders", "Orders you start in Purchasing show here.")
          : {
              state: "ready",
              filters,
              body: {
                kind: "table",
                columns: ["Vendor", "Status", "Total"],
                rows: rows.map((row) => [
                  row["vendorName"] as string,
                  row["status"] === "partially_received" ? "Part received" : typeof row["placedOn"] === "string" ? `Placed ${shortDay(row["placedOn"])}` : "Placed",
                  dollars(row["estimatedTotalCents"] as number),
                ]),
              },
              chip: CHIP.live(),
              emptyTitle: "",
              emptyText: "",
            };
    }
  }
  return out;
}

function attention(dto: Record<string, unknown>, can: (permissions: readonly string[]) => boolean): AttentionRow[] {
  const rows: AttentionRow[] = [];
  const stock = dto["stock"];
  const link = (permissions: readonly string[], label: string, id: string) =>
    can(permissions) ? { action: { label, target: { kind: "action" as const, id } } } : { whoCanFix: "Ask someone who has access to this." };
  if (isPart(stock) && stock.status === "ok") {
    const belowPar = int(stock["belowPar"]);
    if (belowPar !== undefined && belowPar > 0)
      rows.push({
        id: "below-par",
        cause: `${nameList(names(stock["belowParNames"]), belowPar)} ${belowPar === 1 ? "is" : "are"} below par`,
        consequence: "They can run out before the next delivery.",
        ...link(["inv.onhand.view"], "Open stock levels", "#/view/stockLevels"),
      });
  }
  const orders = dto["orders"];
  if (isPart(orders) && orders.status === "ok" && record(orders["late"])) {
    const late = int(orders["late"]["count"]);
    if (late !== undefined && late > 0)
      rows.push({
        id: "orders-late",
        cause: `${plural(late, "order")} placed ${ORDER_DAYS} or more days ago ${late === 1 ? "is" : "are"} not fully received`,
        consequence: "Check with the vendor, or receive what arrived.",
        ...link(["purchasing.order.view"], "Open orders", "#/orders"),
      });
  }
  if (isPart(stock) && stock.status === "ok") {
    const noCost = int(stock["noCost"]);
    if (noCost !== undefined && noCost > 0)
      rows.push({
        id: "no-cost",
        cause: `${nameList(names(stock["noCostNames"]), noCost)} ${noCost === 1 ? "has" : "have"} no cost`,
        consequence: "Inventory value leaves them out until a cost is set.",
        ...link(STOCK_ACCESS, "Open items", "#/settings/inventory"),
      });
  }
  return rows.slice(0, ATTENTION_LIMIT);
}

export interface LocationSourceOptions {
  storageKey: string;
  /** `/tenants/:tenantId/reports` */
  prefix: string;
  locationId: string | undefined;
  request<T>(method: "GET", path: string): Promise<{ status: number; body: T }>;
  /** True when this person holds any of the permissions (the permission snapshot, never a role name). */
  can(permissions: readonly string[]): boolean;
}

export function mapLocationDashboard(dto: unknown, options: Pick<LocationSourceOptions, "can">): DashLoad {
  if (!record(dto) || typeof dto["businessDate"] !== "string") throw new Error("The dashboard did not load.");
  const figures: Record<string, FigureView> = {
    ...salesFigures(dto["sales"], dto["salesTax"]),
    ...(dto["orders"] === undefined ? {} : { "open-orders": orderFigure(dto["orders"]) }),
    ...(dto["stock"] === undefined ? {} : stockFigures(dto["stock"])),
  };
  const name = typeof dto["locationName"] === "string" ? dto["locationName"] : "";
  return {
    scope: `${name === "" ? "" : `${name}, `}${shortDay(dto["businessDate"])}`.trim(),
    attention: attention(dto, options.can),
    figures,
    previews: previews(dto),
  };
}

export function locationDashboardSource(options: LocationSourceOptions): DashboardSource {
  return {
    storageKey: options.storageKey,
    figures: LOCATION_FIGURES,
    previews: LOCATION_PREVIEWS,
    defaults: LOCATION_DEFAULTS,
    tiles: true,
    async load() {
      const query = options.locationId === undefined ? "" : `?locationId=${encodeURIComponent(options.locationId)}`;
      const reply = await options.request<unknown>("GET", `${options.prefix}/dashboard${query}`);
      if (reply.status !== 200) throw new Error("The dashboard did not load.");
      return mapLocationDashboard(reply.body, options);
    },
  };
}
